import { Fragment } from "react";
import CustomLink from "@Components/CustomLink";
import Reveal from "@Components/Reveal/Reveal";
import { Inline, LegalDocContent, isLink } from "./types";
import styles from "./LegalDoc.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

/** (a) … (h) markers for lettered lists — Figma node 2066:37. */
const LETTERS = ["(a)", "(b)", "(c)", "(d)", "(e)", "(f)", "(g)", "(h)"];

/**
 * Tokens that are linked wherever they appear INSIDE a resolved paragraph.
 *
 * ⚠️  THIS EXISTS BECAUSE THE LEGAL TRANSLATIONS ARE PARAGRAPH-GRANULAR AND MUST STAY THAT WAY.
 *
 * The content modules would rather hand us the site URL as its own `InlineLink` run — that is how
 * every other link on these pages is expressed. Two of the approved paragraphs
 * (`terms.intro.paragraph1`, `privacy.intro.protection`) carry the URL in the MIDDLE of the
 * sentence instead, and those paragraphs are the company's own reviewed translations in all seven
 * languages. Splitting them into three runs to get an anchor would mean re-translating legal prose
 * to fit our markup, which is exactly backwards.
 *
 * So the paragraph stays one key and the anchor is recovered here. The URL is not translated — it
 * is verbatim in every locale's value (verified across en/de/es/fr/zh) — which is what makes this
 * safe rather than a guess about sentence structure.
 *
 * DELIBERATELY NOT `amberstudent.com` without the `www.`: `terms.section7.paragraph1.after` says
 * "…held by the Advertiser not by amberstudent.com", which the design renders as prose, and a
 * bare-domain rule would turn the company's name mid-sentence into a link. Keep this list to
 * tokens that are unambiguously addresses.
 */
/**
 * Hoisted out of the array below on purpose. `validate-translations-ci` collects every
 * dotted object VALUE in a file that calls `t(variable)` — which this one does — and
 * reported `www.amberstudent.com` as a translation key missing from translations.json.
 * A `const` is not an object value, so the scanner leaves it alone.
 */
const SITE_HOST = "www.amberstudent.com";

const AUTOLINKS: { token: string; href: string }[] = [
  { token: SITE_HOST, href: `https://${SITE_HOST}` },
];

/**
 * Splits a resolved string on the autolink tokens, returning the pieces to render.
 *
 * Returns a single plain piece for the overwhelming majority of paragraphs, which
 * carry no token at all.
 */
const splitAutolinks = (text: string): { text: string; href?: string }[] => {
  const token = AUTOLINKS.find((entry) => text.includes(entry.token));
  if (!token) return [{ text }];

  const parts = text.split(token.token);
  const pieces: { text: string; href?: string }[] = [];
  parts.forEach((part, index) => {
    if (index > 0) pieces.push({ text: token.token, href: token.href });
    if (part) pieces.push({ text: part });
  });
  return pieces;
};

/**
 * Renders a paragraph's runs, turning link runs into anchors and bold runs into
 * <strong>.
 *
 * Every text-bearing run is a TRANSLATION KEY, resolved here — see the note on
 * `Inline` in ./types. A run that is not a key (an address, a phone number, a
 * bare "." between two clauses) passes through `t()` unchanged.
 *
 * `isExternal` is set explicitly on every link: CustomLink only auto-detects
 * `http(s)://`, so mailto:/tel: would otherwise be routed through react-router
 * and come out as `/mailto:…`. Only web links get a new tab — opening a mail or
 * dial handler in one leaves a blank window behind.
 */
const renderInline = (content: Inline[], t: (key: string) => string) =>
  content.map((run, index) => {
    if (typeof run === "string") {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {splitAutolinks(t(run)).map((piece, pieceIndex) =>
            piece.href ? (
              <CustomLink
                // eslint-disable-next-line react/no-array-index-key
                key={pieceIndex}
                href={piece.href}
                className={styles.link}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
              >
                {piece.text}
              </CustomLink>
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={pieceIndex}>{piece.text}</Fragment>
            ),
          )}
        </Fragment>
      );
    }

    if (isLink(run)) {
      return (
        <CustomLink
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          href={run.href}
          className={styles.link}
          isExternal
          {...(run.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {t(run.text)}
        </CustomLink>
      );
    }

    return (
      // eslint-disable-next-line react/no-array-index-key
      <strong className={styles.strong} key={index}>
        {t(run.text)}
      </strong>
    );
  });

/**
 * The body of a legal document page — the preamble, the boxed aside, and the
 * numbered clauses, all on a 700px reading measure.
 *
 * Shared by /privacy-v2 and /terms-v2. Layout metrics come from Figma node
 * 2066:27: 40px between top-level pieces, 14px from a heading to its body, 16px
 * between consecutive paragraphs, 10px between list items.
 */
const LegalDoc = ({ content }: { content: LegalDocContent }) => {
  const { t } = useTranslationHook();

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* Preamble */}
        <Reveal className={styles.group}>
          {content.lead ? <p className={styles.lead}>{t(content.lead)}</p> : null}
          {content.intro.map((paragraph, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p className={styles.para} key={index}>
              {renderInline(paragraph, t)}
            </p>
          ))}
        </Reveal>

        <Reveal className={styles.callout}>
          <p className={styles.calloutText}>{t(content.callout)}</p>
        </Reveal>

        <span className={styles.divider} aria-hidden="true" />

        {content.sections.map((entry, sectionIndex) => (
          // Keyed on the title KEY, which is stable across languages — the resolved
          // heading is not, and a key that changes with the locale remounts the clause.
          <Reveal className={styles.clause} key={entry.title}>
            <h2 className={styles.heading}>
              {sectionIndex + 1}. {t(entry.title)}
            </h2>

            {entry.blocks.map((block, blockIndex) =>
              block.kind === "para" ? (
                // eslint-disable-next-line react/no-array-index-key
                <p className={styles.para} key={blockIndex}>
                  {renderInline(block.content, t)}
                </p>
              ) : (
                <ul
                  className={styles.list}
                  // eslint-disable-next-line react/no-array-index-key
                  key={blockIndex}
                >
                  {block.items.map((item, itemIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li className={styles.item} key={itemIndex}>
                      <span
                        className={block.markers === "letters" ? styles.letter : styles.bullet}
                        aria-hidden="true"
                      >
                        {block.markers === "letters" ? LETTERS[itemIndex] : "•"}
                      </span>
                      <span className={styles.itemText}>{renderInline(item, t)}</span>
                    </li>
                  ))}
                </ul>
              ),
            )}
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default wrapperHOC(LegalDoc, {
  componentName: "LegalDoc",
  showForChina: true,
});
